/**
 * Outbound webhook destination guard (SSRF) — V114-5, release-coordinator security review of #436.
 *
 * Outbound webhook POSTs go to customer-supplied URLs. This guard is the ONLY gate between a stored
 * URL and a request, and it runs twice for every destination:
 *   1. at registration (createWebhookEndpoint) — refuse before the URL is ever stored;
 *   2. immediately before EVERY send (the dispatcher, the test ping, the manual redeliver) — so a
 *      hostname that resolved to a public address at registration and to a private one now (DNS
 *      rebinding) is refused at send time, never fetched.
 *
 * Pure core (no fetch, no Workers APIs): name resolution goes through the IHostResolver port; the
 * Cloudflare adapter resolves A + AAAA over DNS-over-HTTPS. DEFAULT-DENY: a resolver failure, an
 * empty answer, or ANY resolved address in a refused range refuses the whole destination.
 *
 * Refused:
 *   - anything but https; credentials (userinfo) in the URL; unparseable URLs; a value that is not one
 *     clean absolute URL (whitespace or control characters anywhere — the URL parser would otherwise
 *     percent-encode a pasted sentence into the path and accept it; V114-F-1);
 *   - GaCPro's own hosts: `gacpro.io` and its subdomains, and our `channelmatter.workers.dev` account
 *     (the API Workers), unless the deployment allows that host (`IHostResolver.allowedOwnHosts`, set
 *     only on staging for its webhook test receiver; production refuses every own host) (V114-F-2);
 *   - hostnames `localhost`, `*.localhost`, `*.local`, `*.internal` (incl. metadata.google.internal),
 *     and single-label names (no dot — intranet / search-domain names);
 *   - IPv4 (literal, or encoded as a decimal / hex / octal integer or short form such as
 *     `2130706433`, `0x7f.1`, `0177.0.0.1`): 0/8, 10/8, 100.64/10, 127/8, 169.254/16 (incl. the
 *     169.254.169.254 metadata address), 172.16/12, 192.0.0/24, 192.0.2/24, 192.168/16, 198.18/15,
 *     198.51.100/24, 203.0.113/24, 224/4, 240/4 (incl. 255.255.255.255);
 *   - IPv6: ::/96 (unspecified, loopback, IPv4-compatible), fc00::/7, fe80::/10, ff00::/8,
 *     64:ff9b::/96 (well-known NAT64), 64:ff9b:1::/48 (local-use NAT64), 2002::/16 (6to4 — embeds an
 *     IPv4 address, e.g. 2002:0a00:0001:: carries 10.0.0.1), 2001:0::/32 (Teredo), 100::/64 (discard),
 *     2001:db8::/32 (documentation), and IPv4-mapped ::ffff:0:0/96 checked against the IPv4 list.
 *     The transition ranges are refused wholesale: they are deprecated or local and can tunnel to a
 *     private IPv4 destination.
 */
const MESSAGES = {
    invalid_url: 'The webhook URL is not a valid URL.',
    not_https: 'The webhook URL must be https.',
    userinfo: 'The webhook URL must not contain a username or password.',
    blocked_host: 'The webhook URL points at a local or internal host name. Use a public https endpoint.',
    own_host: 'The webhook URL points at GaCPro itself. Use the https endpoint of your own receiver.',
    blocked_address: 'The webhook URL points at a private, loopback, link-local or reserved address. Use a public https endpoint.',
    resolution_failed: 'The webhook host name could not be resolved. Check the URL and try again.',
    no_addresses: 'The webhook host name has no public address.',
};
const refuse = (reason) => ({ ok: false, reason, message: MESSAGES[reason] });
// ---- IPv4 -----------------------------------------------------------------------------------------
/**
 * inet_aton-style IPv4 parsing: 1–4 parts, each decimal, 0x-hex or 0-octal; the last part fills the
 * remaining bytes (`2130706433`, `0x7f.1`, `127.1`, `0177.0.0.1`). Null when it is not an IPv4 form.
 */
export function parseIPv4(input) {
    const parts = input.split('.');
    if (parts.length === 0 || parts.length > 4)
        return null;
    const nums = [];
    for (const p of parts) {
        if (p === '')
            return null;
        let n;
        if (/^0x[0-9a-f]*$/i.test(p))
            n = p.length === 2 ? 0 : parseInt(p.slice(2), 16);
        else if (/^0[0-7]+$/.test(p))
            n = parseInt(p, 8);
        else if (/^(0|[1-9][0-9]*)$/.test(p))
            n = parseInt(p, 10);
        else
            return null;
        if (!Number.isFinite(n))
            return null;
        nums.push(n);
    }
    const last = nums.pop();
    for (const n of nums)
        if (n > 255)
            return null;
    const lastMax = 2 ** (8 * (4 - nums.length)) - 1;
    if (last > lastMax)
        return null;
    let value = 0;
    for (let i = 0; i < nums.length; i++)
        value += nums[i] * 2 ** (8 * (3 - i));
    return value + last;
}
const V4_BLOCKS = [
    ['0.0.0.0', 8], ['10.0.0.0', 8], ['100.64.0.0', 10], ['127.0.0.0', 8], ['169.254.0.0', 16],
    ['172.16.0.0', 12], ['192.0.0.0', 24], ['192.0.2.0', 24], ['192.168.0.0', 16], ['198.18.0.0', 15],
    ['198.51.100.0', 24], ['203.0.113.0', 24], ['224.0.0.0', 4], ['240.0.0.0', 4],
].map(([a, bits]) => [parseIPv4(a), bits]);
export function isBlockedIPv4(v) {
    return V4_BLOCKS.some(([base, bits]) => {
        const size = 2 ** (32 - bits);
        return v >= base && v < base + size;
    });
}
// ---- IPv6 -----------------------------------------------------------------------------------------
/** Eight 16-bit groups, or null. Accepts `::` compression and a dotted IPv4 tail. */
export function parseIPv6(input) {
    let s = input;
    if (s.startsWith('[') && s.endsWith(']'))
        s = s.slice(1, -1);
    const zone = s.indexOf('%');
    if (zone >= 0)
        s = s.slice(0, zone);
    if (!s.includes(':'))
        return null;
    // A dotted IPv4 tail (`::ffff:127.0.0.1`) becomes two hex groups, then the address parses normally.
    const lastColon = s.lastIndexOf(':');
    const maybeV4 = s.slice(lastColon + 1);
    if (maybeV4.includes('.')) {
        if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(maybeV4))
            return null;
        const b = maybeV4.split('.').map(Number);
        if (b.some((x) => x > 255))
            return null;
        s = `${s.slice(0, lastColon + 1)}${((b[0] << 8) | b[1]).toString(16)}:${((b[2] << 8) | b[3]).toString(16)}`;
    }
    const halves = s.split('::');
    if (halves.length > 2)
        return null;
    const split = (h) => (h === '' ? [] : h.split(':'));
    const head = split(halves[0]);
    const rest = halves.length === 2 ? split(halves[1]) : [];
    for (const g of [...head, ...rest])
        if (!/^[0-9a-f]{1,4}$/i.test(g))
            return null;
    const given = head.length + rest.length;
    if (halves.length === 1 ? given !== 8 : given > 7)
        return null;
    const groups = [
        ...head.map((g) => parseInt(g, 16)),
        ...(halves.length === 2 ? new Array(8 - given).fill(0) : []),
        ...rest.map((g) => parseInt(g, 16)),
    ];
    return groups.length === 8 ? groups : null;
}
export function isBlockedIPv6(g) {
    const [h0, h1, h2, h3, h4, h5, h6, h7] = g;
    // ::/96 — unspecified (::), loopback (::1) and the deprecated IPv4-compatible block.
    if (h0 === 0 && h1 === 0 && h2 === 0 && h3 === 0 && h4 === 0 && h5 === 0)
        return true;
    // IPv4-mapped ::ffff:0:0/96 → the IPv4 rules decide.
    if (h0 === 0 && h1 === 0 && h2 === 0 && h3 === 0 && h4 === 0 && h5 === 0xffff)
        return isBlockedIPv4(h6 * 65536 + h7);
    if ((h0 & 0xfe00) === 0xfc00)
        return true; // fc00::/7 unique local
    if ((h0 & 0xffc0) === 0xfe80)
        return true; // fe80::/10 link-local
    if ((h0 & 0xff00) === 0xff00)
        return true; // ff00::/8 multicast
    if (h0 === 0x64 && h1 === 0xff9b && h2 === 0 && h3 === 0 && h4 === 0 && h5 === 0)
        return true; // 64:ff9b::/96 well-known NAT64
    if (h0 === 0x64 && h1 === 0xff9b && h2 === 0x0001)
        return true; // 64:ff9b:1::/48 local-use NAT64
    if (h0 === 0x2002)
        return true; // 2002::/16 6to4 (embeds an IPv4 address in bits 16–47)
    if (h0 === 0x2001 && h1 === 0x0000)
        return true; // 2001:0::/32 Teredo
    if (h0 === 0x100 && h1 === 0 && h2 === 0 && h3 === 0)
        return true; // 100::/64 discard
    if (h0 === 0x2001 && h1 === 0x0db8)
        return true; // 2001:db8::/32 documentation
    return false;
}
/** True for any address string (v4 in any encoding, or v6) in a refused range; false only for a parsed public address. */
export function isBlockedAddress(address) {
    const v6 = parseIPv6(address);
    if (v6)
        return isBlockedIPv6(v6);
    const v4 = parseIPv4(address);
    if (v4 !== null)
        return isBlockedIPv4(v4);
    return true; // not an address we can classify → refuse (default-deny)
}
// ---- host names -----------------------------------------------------------------------------------
const BLOCKED_SUFFIXES = ['.localhost', '.local', '.internal'];
function isBlockedHostname(host) {
    const h = host.toLowerCase().replace(/\.$/, '');
    if (h === 'localhost' || h === '')
        return true;
    if (BLOCKED_SUFFIXES.some((s) => h.endsWith(s)))
        return true;
    if (!h.includes('.'))
        return true; // single-label (intranet / search-domain) names
    return false;
}
/** GaCPro's own hosts (V114-F-2): the public domain and the API Workers' workers.dev account. */
const OWN_HOST_DOMAINS = ['gacpro.io', 'channelmatter.workers.dev'];
function isOwnHost(host, allowed) {
    if (allowed?.has(host))
        return false;
    return OWN_HOST_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));
}
// ---- the guard ------------------------------------------------------------------------------------
/**
 * The synchronous part: scheme, userinfo, host-name rules and IP-literal ranges (every encoding).
 * `literal` is true when the host is an IP address (no resolution needed). `allowedOwnHosts` is the
 * deployment's exception list for GaCPro's own hosts (none by default).
 */
export function checkWebhookUrlLiteral(url, allowedOwnHosts) {
    // One clean absolute URL only: no whitespace or control characters anywhere (V114-F-1).
    // eslint-disable-next-line no-control-regex
    if (/[\s\u0000-\u001f\u007f]/.test(url))
        return refuse('invalid_url');
    let u;
    try {
        u = new URL(url);
    }
    catch {
        return refuse('invalid_url');
    }
    if (u.protocol !== 'https:')
        return refuse('not_https');
    if (u.username || u.password)
        return refuse('userinfo');
    const host = u.hostname;
    if (!host)
        return refuse('invalid_url');
    // IPv6 literal (URL keeps the brackets).
    if (host.startsWith('[')) {
        const g = parseIPv6(host);
        if (!g)
            return refuse('invalid_url');
        return isBlockedIPv6(g) ? refuse('blocked_address') : { ok: true, host, literal: true };
    }
    // IPv4 in any encoding (the URL parser normalizes most, this catches the rest).
    const v4 = parseIPv4(host);
    if (v4 !== null)
        return isBlockedIPv4(v4) ? refuse('blocked_address') : { ok: true, host, literal: true };
    // A purely numeric-looking label set that did not parse as IPv4 is not a host name either.
    if (/^[0-9.x]+$/i.test(host))
        return refuse('invalid_url');
    if (isBlockedHostname(host))
        return refuse('blocked_host');
    const name = host.toLowerCase().replace(/\.$/, '');
    if (isOwnHost(name, allowedOwnHosts))
        return refuse('own_host');
    return { ok: true, host: name, literal: false };
}
/**
 * The full guard: the literal checks, then — for a host name — resolve A + AAAA and require EVERY
 * address to be public. Run at registration and immediately before every send.
 */
export async function checkWebhookDestination(url, resolver) {
    const lit = checkWebhookUrlLiteral(url, resolver.allowedOwnHosts);
    if (!lit.ok || lit.literal)
        return lit;
    let addresses;
    try {
        addresses = await resolver.resolve(lit.host);
    }
    catch {
        return refuse('resolution_failed');
    }
    if (!addresses.length)
        return refuse('no_addresses');
    if (addresses.some((a) => isBlockedAddress(a)))
        return refuse('blocked_address');
    return lit;
}
//# sourceMappingURL=webhook-url-guard.js.map